import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { getBalances, getBalanceReturns } from "../services/api";
import "../styles/Balances.css";

const AUTO_REFRESH_MS = 15000;

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatAmount = (value, digits = 4) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : numeric.toFixed(digits);
};

const formatCurrency = (value, digits = 2) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : `$${numeric.toFixed(digits)}`;
};

const Balances = ({ data = [], totalUsdt: externalTotalUsdt = null, spotUsdt: externalSpotUsdt = null, futuresUsdt: externalFuturesUsdt = null, onRefresh, loading = false }) => {
  const [internalBalances, setInternalBalances] = useState([]);
  const [internalTotal, setInternalTotal] = useState(0);
  const [internalSpotUsdt, setInternalSpotUsdt] = useState(null);
  const [internalFuturesUsdt, setInternalFuturesUsdt] = useState(null);
  const [internalLoading, setInternalLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [returns, setReturns] = useState(null);

  const useExternalData = Array.isArray(data) && typeof onRefresh === "function";

  const fetchInternalBalances = useCallback(async () => {
    setInternalLoading(true);
    try {
      const response = await getBalances();
      const balances = Array.isArray(response?.balances) ? response.balances : [];
      const total = toNumber(response?.total_usdt) ?? 0;

      setInternalBalances(balances);
      setInternalTotal(total);
      setInternalSpotUsdt(toNumber(response?.spot_usdt));
      setInternalFuturesUsdt(toNumber(response?.futures_usdt));
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("❌ Error al obtener balances:", err);
      setError("No se pudieron obtener balances");
    } finally {
      setInternalLoading(false);
    }
  }, []);

  const fetchReturns = useCallback(async () => {
    try {
      const data = await getBalanceReturns();
      if (data) setReturns(data);
    } catch (err) {
      console.error("Error fetching returns:", err);
    }
  }, []);

  useEffect(() => {
    fetchReturns();
    const interval = setInterval(fetchReturns, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchReturns]);

  useEffect(() => {
    if (useExternalData) return undefined;

    fetchInternalBalances();
    const interval = setInterval(fetchInternalBalances, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchInternalBalances, useExternalData]);

  useEffect(() => {
    if (!useExternalData) return;
    setLastUpdate(new Date());
    setError(null);
  }, [data, useExternalData]);

  const balances = useMemo(() => {
    const source = useExternalData ? data : internalBalances;
    const list = Array.isArray(source) ? source : [];

    return [...list]
      .map((item) => ({
        ...item,
        freeNumber: toNumber(item?.free) ?? 0,
        frozenNumber: toNumber(item?.frozen) ?? 0,
        usdtValueNumber: toNumber(item?.usdt_value),
      }))
      .sort((a, b) => {
        const aValue = a.usdtValueNumber ?? 0;
        const bValue = b.usdtValueNumber ?? 0;
        return bValue - aValue;
      });
  }, [data, internalBalances, useExternalData]);

  const totalUsdt = useMemo(() => {
    if (!useExternalData) return internalTotal;

    const providedTotal = toNumber(externalTotalUsdt);
    if (providedTotal !== null) return providedTotal;

    const total = balances.reduce((acc, item) => {
      if (item.usdtValueNumber !== null) return acc + item.usdtValueNumber;
      if (String(item.coin || "").toUpperCase() === "USDT") {
        return acc + item.freeNumber + item.frozenNumber;
      }
      return acc;
    }, 0);

    return total;
  }, [balances, externalTotalUsdt, internalTotal, useExternalData]);

  const crossSpotUsdt = useExternalData ? externalSpotUsdt : internalSpotUsdt;
  const crossFuturesUsdt = useExternalData ? externalFuturesUsdt : internalFuturesUsdt;

  const activeCoins = balances.filter((item) => item.freeNumber > 0 || item.frozenNumber > 0).length;
  const lockedCoins = balances.filter((item) => item.frozenNumber > 0).length;

  const isLoading = useExternalData ? Boolean(loading && balances.length === 0) : Boolean(internalLoading && balances.length === 0);

  const handleRefresh = async () => {
    if (useExternalData) {
      try {
        await onRefresh();
        setLastUpdate(new Date());
      } catch (err) {
        console.error("❌ Error refrescando balances externos:", err);
        setError("No se pudo refrescar balances");
      }
      return;
    }

    await fetchInternalBalances();
  };

  if (error && balances.length === 0) {
    return (
      <Card className="balances-panel-card">
        <Card.Header className="balances-panel-header">
          <h5 className="balances-panel-title">💰 Balances</h5>
        </Card.Header>

        <Card.Body className="balances-panel-body">
          <div className="balances-panel-state balances-panel-state-error">
            <p>❌ {error}</p>
            <button type="button" onClick={handleRefresh} className="balances-panel-btn">
              🔄 Reintentar
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="balances-panel-card">
        <Card.Header className="balances-panel-header">
          <h5 className="balances-panel-title">💰 Balances</h5>
        </Card.Header>

        <Card.Body className="balances-panel-body">
          <div className="balances-panel-state">
            <div className="balances-panel-spinner" />
            <p>⏳ Cargando balances...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (balances.length === 0) {
    const hasSummaryOnlyTotal = Number.isFinite(totalUsdt) && totalUsdt > 0;
    if (hasSummaryOnlyTotal) {
      return (
        <Card className="balances-panel-card">
          <Card.Header className="balances-panel-header">
            <div className="balances-panel-header-row">
              <h5 className="balances-panel-title">💰 Balances</h5>
              <span className="balances-panel-time">
                {lastUpdate ? `Actualizado: ${lastUpdate.toLocaleTimeString()}` : "Actualizando..."}
              </span>
            </div>
          </Card.Header>

          <Card.Body className="balances-panel-body">
            <section className="balances-panel-hero">
              <div className="balances-panel-total-wrap">
                <span className="balances-panel-total-label">Total estimado</span>
                <strong className="balances-panel-total-value">{formatCurrency(totalUsdt, 2)}</strong>
                <small className="balances-panel-total-sub">USDT equivalente</small>
              </div>

              <div className="balances-panel-kpis">
                <div className="balances-panel-kpi-card">
                  <span className="balances-panel-kpi-label">Monedas activas</span>
                  <span className="balances-panel-kpi-value">1</span>
                </div>
                <div className="balances-panel-kpi-card">
                  <span className="balances-panel-kpi-label">Con bloqueo</span>
                  <span className="balances-panel-kpi-value">0</span>
                </div>
              </div>
            </section>

            <div className="balances-panel-account-split">
              <div className="balances-panel-account-card">
                <span className="balances-panel-account-label">💰 Spot</span>
                <span className="balances-panel-account-value">
                  {formatCurrency(crossSpotUsdt ?? 0, 2)}
                </span>
              </div>
              <div className="balances-panel-account-card">
                <span className="balances-panel-account-label">📈 Futuros</span>
                <span className="balances-panel-account-value">
                  {formatCurrency(crossFuturesUsdt ?? 0, 2)}
                </span>
              </div>
            </div>

            <div className="balances-panel-empty">
              <p>Saldo simulado disponible</p>
              <button type="button" className="balances-panel-btn" onClick={handleRefresh}>
                🔄 Actualizar
              </button>
            </div>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card className="balances-panel-card">
        <Card.Header className="balances-panel-header">
          <h5 className="balances-panel-title">💰 Balances</h5>
        </Card.Header>

        <Card.Body className="balances-panel-body">
          <div className="balances-panel-empty">
            <p>📭 Sin balances disponibles</p>
            <button type="button" className="balances-panel-btn" onClick={handleRefresh}>
              🔄 Actualizar
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="balances-panel-card">
      <Card.Header className="balances-panel-header">
        <div className="balances-panel-header-row">
          <h5 className="balances-panel-title">💰 Balances</h5>
          <span className="balances-panel-time">{lastUpdate ? `Actualizado: ${lastUpdate.toLocaleTimeString()}` : "Actualizando..."}</span>
        </div>
      </Card.Header>

      <Card.Body className="balances-panel-body">
        <section className="balances-panel-hero">
          <div className="balances-panel-total-wrap">
            <span className="balances-panel-total-label">Total estimado</span>
            <strong className="balances-panel-total-value">{formatCurrency(totalUsdt, 2)}</strong>
            <small className="balances-panel-total-sub">USDT equivalente</small>
          </div>

          <div className="balances-panel-kpis">
            <div className="balances-panel-kpi-card">
              <span className="balances-panel-kpi-label">Monedas activas</span>
              <span className="balances-panel-kpi-value">{activeCoins}</span>
            </div>
            <div className="balances-panel-kpi-card">
              <span className="balances-panel-kpi-label">Con bloqueo</span>
              <span className="balances-panel-kpi-value">{lockedCoins}</span>
            </div>
          </div>
        </section>

        <div className="balances-panel-account-split">
          <div className="balances-panel-account-card">
            <span className="balances-panel-account-label">💰 Spot</span>
            <span className="balances-panel-account-value">
              {formatCurrency(crossSpotUsdt ?? 0, 2)}
            </span>
          </div>
          <div className="balances-panel-account-card">
            <span className="balances-panel-account-label">📈 Futuros</span>
            <span className="balances-panel-account-value">
              {formatCurrency(crossFuturesUsdt ?? 0, 2)}
            </span>
          </div>
        </div>

        {returns && (
          <section className="balances-panel-returns">
            <div className="balances-panel-returns-header">Rendimiento estimado</div>
            <div className="balances-panel-returns-grid">
              {["24h", "48h", "7d", "15d", "30d"].map((period) => {
                const r = returns[period];
                if (!r) return null;
                const sign = r.pct >= 0 ? "+" : "";
                const cls = r.pct >= 0 ? "returns-positive" : "returns-negative";
                return (
                  <div key={period} className="balances-panel-returns-item">
                    <span className="balances-panel-returns-label">{period}</span>
                    <span className={`balances-panel-returns-value ${cls}`}>{sign}{r.pct.toFixed(2)}%</span>
                    <span className="balances-panel-returns-pnl">{formatCurrency(r.pnl)}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="balances-panel-grid">
          {balances.map((balance) => {
            const key = `${balance.coin || "COIN"}-${balance.free}-${balance.frozen}`;
            const hasFrozen = balance.frozenNumber > 0;
            const hasUsdtValue = balance.usdtValueNumber !== null;

            return (
              <article key={key} className="balances-panel-item">
                <header className="balances-panel-item-head">
                  <span className="balances-panel-coin">
                    <img
                      src={`${process.env.PUBLIC_URL}/immoneda.png`}
                      alt=""
                      className="balances-panel-coin-icon"
                    />
                    {String(balance.coin || "N/A").toUpperCase()}
                  </span>
                  {hasFrozen ? <span className="balances-panel-lock">🔒</span> : <span className="balances-panel-free">Libre</span>}
                </header>

                <div className="balances-panel-main-amount">{formatAmount(balance.freeNumber, 6)}</div>

                <div className="balances-panel-item-meta">
                  <span>Disponible</span>
                  <span>{formatAmount(balance.freeNumber, 6)}</span>
                </div>

                <div className="balances-panel-item-meta">
                  <span>Congelado</span>
                  <span>{formatAmount(balance.frozenNumber, 6)}</span>
                </div>

                <div className="balances-panel-item-value">
                  {hasUsdtValue ? `≈ ${formatCurrency(balance.usdtValueNumber, 2)}` : "Sin conversión"}
                </div>
              </article>
            );
          })}
        </div>

        <button type="button" className="balances-panel-btn balances-panel-btn-full" onClick={handleRefresh}>
          🔄 Actualizar Balances
        </button>
      </Card.Body>
    </Card>
  );
};

export default React.memo(Balances);
